import { Component } from './component.js';

/**
 * A custom element that manages a row of tabs.
 */
class TabRow extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.headerContainer = this.querySelector('.tab-row__header-container');
    this.externalTriggers = Array.from(this.querySelectorAll('.tab-row__header-button'));
    
    // Fallback: If layout is tabs but no external triggers rendered (Liquid limitation), generate them from content.
    if (this.headerContainer && this.externalTriggers.length === 0) {
      this.#generateExternalTriggers();
    } else {
      this.#bindExternalTriggers();
    }
    
    this.#initTabs();
  }

  #generateExternalTriggers() {
    const items = this.querySelectorAll('tab-row-item');
    
    items.forEach((item, index) => {
      const internalHeader = item.querySelector('.tab-row-item__header');
      if (!internalHeader) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tab-row__header-button';
      button.innerHTML = internalHeader.innerHTML; // Copy icon and text
      button.setAttribute('data-tab-trigger', '');
      button.setAttribute('data-index', index);
      
      // Copy attributes if needed
      if (item.hasAttribute('active-by-default')) {
         // managed by initTabs
      }

      button.addEventListener('click', () => {
        this.activateTab(items[index]);
      });

      this.headerContainer.appendChild(button);
      this.externalTriggers.push(button);
    });
  }

  #bindExternalTriggers() {
    this.externalTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => {
        const items = this.querySelectorAll('tab-row-item');
        // Use data-index if available (more robust if blocks reordered/filtered)
        const dataIndex = trigger.getAttribute('data-index');
        const targetIndex = dataIndex !== null ? parseInt(dataIndex) : index;
        
        if (items[targetIndex]) {
          this.activateTab(items[targetIndex]);
        }
      });
    });
  }

  #initTabs() {
    const items = this.querySelectorAll('tab-row-item');
    if (items.length === 0) return;

    // Set initial state
    let hasActive = false;
    
    // Check if any external trigger is active (unlikely on load unless preserved)
    // Or check items
    for (const item of items) {
      if (item.hasAttribute('active-by-default')) {
        this.activateTab(item);
        hasActive = true;
        break;
      }
    }

    if (!hasActive) {
      this.activateTab(items[0]);
    }
  }

  /**
   * Activates a specific tab item.
   * @param {HTMLElement} targetItem - The tab item to activate.
   */
  activateTab(targetItem) {
    const items = this.querySelectorAll('tab-row-item');
    let targetIndex = -1;

    items.forEach((item, index) => {
      const isActive = item === targetItem;
      item.toggleAttribute('active', isActive);

      if (isActive) targetIndex = index;

      // internal trigger handling (accordion / mobile)
      const button = item.querySelector('.tab-row-item__header');
      if (button) {
        button.setAttribute('aria-expanded', isActive.toString());
      }
    });

    // Update external triggers
    if (this.externalTriggers.length > 0) {
      this.externalTriggers.forEach((trigger, index) => {
        const isTriggerActive = index === targetIndex;
        trigger.setAttribute('aria-expanded', isTriggerActive.toString());
      });
    }
  }
}

/**
 * A custom element representing a single tab item.
 */
class TabRowItem extends HTMLElement {
  connectedCallback() {
    // Internal trigger (for accordion mode)
    const trigger = this.querySelector('.tab-row-item__header');
    if (trigger) {
      trigger.addEventListener('click', this.#onTriggerClick.bind(this));
    }
  }

  #onTriggerClick() {
    const parent = this.closest('tab-row');
    if (parent && typeof parent.activateTab === 'function') {
      parent.activateTab(this);
    }
  }
}

if (!customElements.get('tab-row')) {
  customElements.define('tab-row', TabRow);
}

if (!customElements.get('tab-row-item')) {
  customElements.define('tab-row-item', TabRowItem);
}
